import React from "react";
import {
  Box,
  Chip,
  Divider,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { MoreHorizontal, Shuffle, WarningOutline } from "@tiny/icons";
import { useDrawerInvokers } from "@tiny/lib";
import { Handle, NodeProps, Position, useUpdateNodeInternals } from "reactflow";
import { NodeData, SplitBranch } from "../types";
import { MenuIconButton } from "../../../../components";
import useDeleteNode from "../Hooks/useDeleteNode";
import DeleteSplitDialog from "../Hooks/DeleteSplitDialog";
import TrafficSplitForm from "../../../../customComponents/TrafficSplitForm";
import SplitStatsPanel from "../../../../customComponents/SplitStatsPanel";
import { useWorkflowsBuilderPageView } from "../../WorkflowsBuilderPage.context";
import {
  getBranchHandleLabel,
  getSplitModeLabel,
  getSplitSummary,
  getUnconnectedBranches,
} from "../Split/split.helpers";

const SplitNode = ({ id, data }: NodeProps<NodeData>) => {
  const { deleteSplit } = useDeleteNode();
  const { showDrawer } = useDrawerInvokers();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { viewMode, showNodeStats, workflowId, workflowData, splitPreview } =
    useWorkflowsBuilderPageView();
  const { configured } = data.config;

  // While the settings panel is open the canvas follows the unsaved form.
  const isPreview = splitPreview?.nodeId === id;
  const splitData = isPreview ? splitPreview.data : data;
  const branches: SplitBranch[] = splitData.branches || [];
  const unconnectedIds = isPreview
    ? new Set<string>()
    : new Set(
        getUnconnectedBranches(id, branches, workflowData.structure.edges).map(
          (branch) => branch.id
        )
      );

  // Handles come and go with the branches; React Flow has to re-measure them
  // before an edge can attach to a new one.
  const updateNodeInternals = useUpdateNodeInternals();
  const handleIds = branches.map((branch) => branch.id).join(",");
  React.useEffect(() => {
    updateNodeInternals(id);
  }, [id, handleIds, updateNodeInternals]);

  const openSettings = () => {
    showDrawer({ children: <TrafficSplitForm id={id} /> });
  };

  const openResults = () => {
    showDrawer({
      children: <SplitStatsPanel workflowId={workflowId} nodeId={id} />,
    });
  };

  const handleClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (showNodeStats && configured) {
      openResults();
    } else {
      openSettings();
    }
  };

  const handleConfirmDelete = (keepBranchId: string | null) => {
    setDialogOpen(false);
    deleteSplit(id, keepBranchId);
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ backgroundColor: "transparent", border: "none" }}
      />
      <Box>
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[80]}`,
            padding: 3,
            paddingBottom: 4,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            width: "400px",
            cursor: "pointer",
          })}
          onClick={handleClick}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mr: -1 }}
          >
            <Box display="flex" alignItems="center">
              <Shuffle
                sx={(theme) => ({
                  fontSize: 20,
                  mr: 1,
                  color: theme.palette.fuchsia[150],
                })}
              />
              <Typography
                variant="label1"
                sx={(theme) => ({
                  textTransform: "uppercase",
                  color: theme.palette.fuchsia[150],
                })}
              >
                LOGIC {"›"} {data.label}
              </Typography>
            </Box>
            <MenuIconButton
              icon={<MoreHorizontal />}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem onClick={openSettings}>
                {viewMode ? "Preview" : "Edit"}
              </MenuItem>
              {showNodeStats && (
                <MenuItem onClick={openResults}>View results</MenuItem>
              )}
              {!viewMode && (
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setDialogOpen(true);
                  }}
                >
                  Delete
                </MenuItem>
              )}
            </MenuIconButton>
          </Box>
          <Box>
            <Typography
              variant="p2"
              color="grey.100"
              fontWeight="600"
              data-testid="split-summary"
            >
              {getSplitSummary(splitData)}
            </Typography>
            <Typography
              variant="label2"
              color="grey.80"
              component="div"
              data-testid="split-mode"
            >
              {getSplitModeLabel(splitData)}
            </Typography>
          </Box>
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <Chip
              sx={(theme) => ({
                height: theme.spacing(5),
                backgroundColor: configured
                  ? theme.palette.primary[100]
                  : theme.palette.yellow[150],
                color: theme.palette.common.white,
                mr: 1,
                fontSize: theme.spacing(3),
                lineHeight: theme.spacing(3),
                "& .MuiChip-label": { fontWeight: 700 },
              })}
              label={configured ? "Configured" : "Not-Configured"}
            />
            {showNodeStats && configured && (
              <Typography variant="label2" color="primary.125">
                View results
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-around" gap={1}>
          {branches.map((branch) => {
            const unconnected = unconnectedIds.has(branch.id);
            return (
              <Box
                key={branch.id}
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Divider
                  orientation="vertical"
                  sx={{ height: "20px" }}
                  color="grey.60"
                />
                <Tooltip
                  title={
                    unconnected
                      ? `${branch.name} has no connection`
                      : getBranchHandleLabel(branch)
                  }
                  arrow
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={(theme) => ({
                      background: theme.palette.common.white,
                      border: `1px solid ${
                        unconnected
                          ? theme.palette.red[100]
                          : theme.palette.grey[80]
                      }`,
                      px: 2,
                      py: 2,
                      borderRadius: 3,
                      maxWidth: "100%",
                    })}
                  >
                    {unconnected && (
                      <WarningOutline
                        sx={(theme) => ({
                          fontSize: 14,
                          color: theme.palette.red[100],
                        })}
                      />
                    )}
                    <Typography
                      variant="label2"
                      color="grey.80"
                      noWrap
                      data-testid={`split-branch-${branch.id}`}
                    >
                      {getBranchHandleLabel(branch)}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </Box>
      {branches.map((branch, index) => (
        <Handle
          key={branch.id}
          type="source"
          position={Position.Bottom}
          id={branch.id}
          style={{
            left: `${((index + 0.5) / branches.length) * 100}%`,
            backgroundColor: "transparent",
            border: "none",
          }}
        />
      ))}
      <DeleteSplitDialog
        open={dialogOpen}
        branches={data.branches || []}
        onCancel={() => setDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default SplitNode;
